import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api, errMsg } from "../../lib/api";

const STATUS_CLS = {
  SIAP: "bg-emerald-600 text-white",
  TERDAFTAR: "bg-emerald-600 text-white",
  "SUDAH TERDAFTAR": "bg-slate-300 text-slate-700",
  DUPLIKAT: "bg-slate-300 text-slate-700",
  "EMAIL TIDAK VALID": "bg-rose-600 text-white",
  "POLDA TIDAK VALID": "bg-rose-600 text-white",
};

const parse = (text) =>
  text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const parts = l.split(/[;,\t|]/).map((s) => s.trim()).filter(Boolean);
    const email = parts.find((s) => s.includes("@")) || parts[0] || "";
    const rest = parts.filter((s) => s !== email);
    const polda = rest.find((s) => /polda/i.test(s)) || rest[0] || "";
    const name = rest.filter((s) => s !== polda).join(" ");
    return { email, polda, name };
  });

const Preview = ({ rows }) => (
  <div className="border max-h-60 overflow-auto" data-testid="bulk-preview">
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-muted">
        <tr className="text-left"><th className="px-3 py-2">Email</th><th className="px-3 py-2">Polda</th><th className="px-3 py-2">Nama</th><th className="px-3 py-2">Status</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.email}-${i}`} className="border-t">
            <td className="px-3 py-1.5 font-mono">{r.email || "-"}</td>
            <td className="px-3 py-1.5">{r.polda || "-"}</td>
            <td className="px-3 py-1.5">{r.name || "-"}</td>
            <td className="px-3 py-1.5"><span className={`px-1.5 py-0.5 rounded-sm font-semibold whitespace-nowrap ${STATUS_CLS[r.status]}`}>{r.status}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const BulkUserDialog = ({ open, onClose, onSaved }) => {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setText("");
      setPreview(null);
    }
  }, [open]);

  const run = async (dry) => {
    const entries = parse(text);
    if (!entries.length) return toast.error("Isi minimal satu baris");
    setBusy(true);
    try {
      const { data } = await api.post("/users/bulk", { entries, dry_run: dry });
      setPreview(data);
      if (!dry) {
        toast.success(`${data.created} akun Polda didaftarkan`);
        onSaved();
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const suggest = async () => {
    try {
      const { data } = await api.get("/users/suggestions");
      if (!data.length) return toast.info("Tidak ada email baru dari data Direction Finder Polda");
      setText(data.map((s) => `${s.email}, ${s.polda}`).join("\n"));
      setPreview(null);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="bulk-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Undang Akun Polda Sekaligus</DialogTitle>
          <DialogDescription>Satu baris per akun: <span className="font-mono">email, POLDA, nama (opsional)</span>. Semua didaftarkan sebagai Ditressiber Polda.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Button type="button" variant="outline" size="sm" onClick={suggest} data-testid="bulk-suggest-button">
            <Wand2 className="h-4 w-4" /> Ambil email PIC dari data Direction Finder Polda
          </Button>
          <Textarea rows={8} value={text} onChange={(e) => { setText(e.target.value); setPreview(null); }}
            placeholder={"ditressiber.jatim@gmail.com, POLDA JATIM, AKBP Contoh\nditressiber.bali@gmail.com; POLDA BALI"}
            className="font-mono text-xs" data-testid="bulk-textarea" />
          {preview && <Preview rows={preview.results} />}
          {preview && <p className="text-xs text-muted-foreground" data-testid="bulk-summary">{preview.created ? `${preview.created} akun berhasil didaftarkan.` : `${preview.ready} akun siap didaftarkan.`}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="bulk-preview-button">Pratinjau</Button>
          <Button onClick={() => run(false)} disabled={busy || !preview || !preview.ready || preview.created > 0} data-testid="bulk-submit-button">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Daftarkan {preview?.ready && !preview.created ? preview.ready : ""} Akun
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
